import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ShoppingCart, Package, Tag, Tags, BarChart3, UtensilsCrossed,
  LayoutGrid, Users, Settings, CreditCard, ClipboardList,
  LogOut, ChevronLeft, ChevronRight, Menu, TicketCheck,
  FileText, Sparkles, Globe, Bell, History, MessageCircle, Workflow, Kanban, LayoutDashboard,
  Calendar as CalendarIcon, Braces, MapPin
} from 'lucide-react';
import API_URL from '../../config/api';
import apiClient from '../../utils/apiClient';
import CajaVentas from './CajaVentas';
import ProductosManager from './ProductosManager';
import CategoriasManager from './CategoriasManager';
import ReportesUnificado from './ReportesUnificado';
import CocinaDisplay from './CocinaDisplay';
import MesasManager from './MesasManager';
import PedidosPendientes from './PedidosPendientes';
import SoporteTickets from './SoporteTickets';
import EmpleadosManager from './EmpleadosManager';
import Configuracion from './Configuracion';
import AlertasManager from './AlertasManager';
import Contactos from './Contactos';
import MenuWebManager from './MenuWebManager';
import AuditoriaLog from './AuditoriaLog';
import Conversaciones from './Conversaciones';
import PipelineManager from './PipelineManager';
import DashboardCRM from './DashboardCRM';
import WorkflowsManager from './WorkflowsManager';
import EtiquetasManager from './EtiquetasManager';
import CalendariosManager from './CalendariosManager';
import PaginasManager from './PaginasManager';
import PlantillasManager from './PlantillasManager';
import ContextosIAManager from './ContextosIAManager';
import CustomFieldsManager from './CustomFieldsManager';
import LocationsManager from './LocationsManager';
import PortalCliente from './PortalCliente';
import FacturasManager from './FacturasManager';
import CustomAlert from './shared/CustomAlert';

function DashboardPOS({ clientData, token, onLogout, onProfileUpdate }) {
  // El servidor entrega snake_case y App.js lo normaliza a camelCase. Se
  // aceptan ambas formas como salvaguarda durante la carga o tras restaurar
  // una sesión anterior: una cuenta de Servicios nunca debe caer en Caja.
  const productType = clientData?.productType || clientData?.product_type || 'pos';
  // ⭐ Vista de entrada (pedido de Angelo, 2026-08-07: "dentro de la interfaz
  // del pos del crm, quiero que lo primero que salga sea el dashboard crm").
  // 'caja' como default solo tiene sentido para cuentas de POS -- para una
  // cuenta CRM-only (productType==='crm') ni siquiera existe esa pestaña, así
  // que antes de este fix el usuario aterrizaba donde cayera el primer tab
  // visible del array de abajo (que por orden de definición resultaba ser
  // "Contactos", no "Dashboard CRM" como pidió Angelo -- ver el fallback de
  // más abajo, que sigue existiendo como red de seguridad para el resto de
  // los casos, pero ya no decide el aterrizaje inicial de las cuentas CRM).
  const [activeTab, setActiveTab] = useState(() => (
    productType === 'crm'
      ? 'dashboard-crm'
      : productType === 'services' ? 'portal-cliente' : 'caja'
  ));
  const [stats, setStats] = useState({ total_sales: 0, total_revenue: 0 });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bgColor, setBgColor] = useState(
    localStorage.getItem('pos_background_color') || '#f3f4f6'
  );
  const [comingSoon, setComingSoon] = useState(null);
  const [managedChangeNotice, setManagedChangeNotice] = useState(null);
  // ⭐ Módulo CRM/Conversaciones: el acceso NO es un simple plan-gate como el
  // resto de secciones (planAtLeast) -- backend.hasCrmAccess() es un OR entre
  // un flag manual (crm_enabled, para ventas standalone a Básico/Estándar) y
  // Premium+/activo. Esa lógica vive solo en el backend (ver /api/pos/crm/access
  // en index.js); acá simplemente se consulta, en vez de duplicarla en el
  // frontend y arriesgar que se desincronicen.
  const [crmAccess, setCrmAccess] = useState(false);
  // ⭐ Menú Web Dinámico (pedido de Angelo, 2026-08-05): addon puro activado
  // a mano por el admin (menu_dinamico_enabled en clients), sin plan-gating
  // ni prueba gratuita -- a diferencia de crmAccess/menuAccess que sí mezclan
  // plan+trial. Mientras no esté activado, la pestaña ni aparece en el menú
  // (antes era una pestaña "Próximamente" visible para todo dueño).
  const [menuDinamicoAccess, setMenuDinamicoAccess] = useState(false);
  // ⭐ Menú Web Estático como addon vendible (2026-08-08). Aditivo al plan: la
  // pestaña aparece si el plan ya lo incluye (Premium+) O si el admin lo activó
  // a mano para esta cuenta. Sin esto, una cuenta CRM-only nunca podía tenerlo
  // porque no tiene plan de POS contra el cual comparar.
  const [menuEstaticoAccess, setMenuEstaticoAccess] = useState(false);

  const permissions = clientData?.permissions || {};
  // ⭐ sessionStorage (no localStorage): la identidad de sesión no debe
  // compartirse entre pestañas — ver nota en App.js sobre el bug de fuga
  // de sesión entre pestañas (2026-07-28).
  const userType = sessionStorage.getItem('userType');
  const isOwner = userType === 'cliente';
  const isEmployee = userType === 'employee';

  // ⭐ Roles con nombre amigable. El unico valor real que existe hoy en la
  // BD para empleados creados antes del selector de rol es 'employee', asi
  // que se mapea a "Empleado" como fallback razonable.
  const ROLE_LABELS = {
    employee: 'Empleado',
    personalizado: 'Empleado',
    mesero: 'Mesero',
    cocinero: 'Cocinero',
    cajero: 'Cajero',
    gerente: 'Gerente'
  };
  const employeeRoleLabel = ROLE_LABELS[clientData?.role] || clientData?.role || 'Empleado';

  // ⭐ Plan-gating de secciones: ademas del permiso RBAC, algunas secciones
  // ahora tambien requieren un plan minimo (ej. Cocina/Empleados desde
  // Estandar $25). Aplica por igual a dueño y empleado porque el plan es
  // de la CUENTA, no de la persona — clientData.plan viene de la misma
  // sessionStorage('clientPlan') para ambos casos (ver App.js).
  // Las secciones que todavia estan en "Pronto" (comingSoon:true) NO se
  // ocultan por plan — quedan visibles con su mensaje hasta que se
  // desarrollen para todos, tal como se decidio explicitamente.
  const PLAN_RANK = { basico: 0, estandar: 1, premium: 2, empresarial: 3 };
  const currentPlanRank = PLAN_RANK[clientData?.plan] ?? 0;
  const planAtLeast = (minPlan) => currentPlanRank >= (PLAN_RANK[minPlan] ?? 0);

  // ⭐ Cuenta CRM-only (pedido de Angelo, 2026-08-06: registro standalone del
  // producto CRM desde marketos-landing /crm, ver App.js/clientData.productType).
  // Estas cuentas nunca tuvieron ni tendrán punto de venta -- el sidebar les
  // muestra ÚNICAMENTE las secciones de CRM (nunca Caja/Mesas/Cocina/Inventario,
  // ni con "Próximamente": pedido explícito de que solo vean lo ya construido).
  // `clientData.plan` en estas cuentas queda en 'basico' desde el registro
  // (irrelevante para el CRM, ver crm_plan_tier en el backend), así que el
  // filtro de tabs de más abajo no puede depender de planAtLeast() para decidir
  // qué mostrarles -- se filtra por whitelist de ids en su lugar.
  const isCrmOnly = productType === 'crm';
  const isServicesOnly = productType === 'services';
  // Mismos 3 tiers que CRM_PLAN_TIERS en Dashboard.js (admin) -- acá solo se
  // necesita el label corto para el badge del sidebar, no la descripción larga.
  const CRM_PLAN_TIER_LABELS = { emprendedor: 'Emprendedor', ia_plus: 'IA Plus', business: 'Business' };
  // ⭐ 'empleados' sumado 2026-08-08 (pedido de Angelo: "no tengo empleados
  // en CRM a pesar de que estoy en un plan business"). Decisión explícita
  // de Angelo al preguntarle qué tiers lo desbloquean: planes 2 y 3
  // (ia_plus/business) sí, el más barato (emprendedor) no -- ver
  // CRM_EMPLOYEES_MIN_TIER más abajo, que es lo que realmente decide esto
  // (acá solo hace falta que el id esté en la whitelist).
  // ⭐ 'paginas' y 'menu-web' entran a la lista (2026-08-08): una cuenta CRM-only
  // es justamente la que más necesita desplegar landings y capturar leads. Igual
  // no aparecen si el admin no activó el addon para esa cuenta -- estar en esta
  // lista solo significa "no queda excluido por ser CRM-only", el gate real sigue
  // siendo el `show` de cada tab.
  const CRM_ONLY_TAB_IDS = new Set(['dashboard-crm', 'conversaciones', 'pipeline', 'contactos', 'workflows', 'plantillas', 'contextos-ia', 'campos-personalizados', 'sedes', 'etiquetas', 'calendarios', 'paginas', 'menu-web', 'empleados', 'configuracion', 'soporte', 'portal-cliente']);
  const SERVICES_ONLY_TAB_IDS = new Set(['portal-cliente', 'soporte', 'configuracion']);
  // Mismo criterio que arriba: qué tiers de CRM_PLAN_TIERS (Dashboard.js
  // admin) habilitan el módulo de Empleados para una cuenta CRM-only.
  const CRM_EMPLOYEES_MIN_TIER = new Set(['ia_plus', 'business']);

  useEffect(() => {
    loadStats();
    // ⭐ Las ventas/pedidos pueden cambiar desde otras pestañas (Cocina, Mesas,
    // Pedidos) que no notifican a este componente. Se refresca periódicamente
    // (mismo patrón ya usado para el auto-refresco de mesas en CajaVentas) para
    // que "Ventas de hoy" no quede desactualizado tras anular/cobrar en otra vista.
    const interval = setInterval(loadStats, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    apiClient.get('/api/pos/crm/access')
      .then(res => setCrmAccess(Boolean(res.data?.hasCrmAccess)))
      .catch(() => setCrmAccess(false));
  }, []);

  useEffect(() => {
    // El endpoint devuelve los dos addons de web en una sola llamada.
    apiClient.get('/api/pos/menu-dinamico/access')
      .then(res => {
        setMenuDinamicoAccess(Boolean(res.data?.hasAccess));
        setMenuEstaticoAccess(Boolean(res.data?.menuEstatico));
      })
      .catch(() => { setMenuDinamicoAccess(false); setMenuEstaticoAccess(false); });
  }, []);

  useEffect(() => {
    const showManagedChangeNotice = (event) => {
      setManagedChangeNotice({
        type: 'success',
        title: 'Solicitud recibida',
        message: event.detail?.message || 'Tu solicitud fue recibida. En breve nuestro equipo de desarrollo se pondrá en contacto contigo. Gracias por confiar en MARKETOS.'
      });
    };
    window.addEventListener('marketos:approval-requested', showManagedChangeNotice);
    return () => window.removeEventListener('marketos:approval-requested', showManagedChangeNotice);
  }, []);

  useEffect(() => {
    // Aún existen módulos antiguos que usan axios directamente. Mientras se
    // migran de forma segura al cliente compartido, este interceptor mantiene
    // el aviso uniforme para esas solicitudes aprobables también.
    const interceptorId = axios.interceptors.response.use((response) => {
      if (response.status === 202 && response.data?.approval_required) {
        setManagedChangeNotice({
          type: 'success',
          title: 'Solicitud recibida',
          message: response.data.message || 'Tu solicitud fue recibida. En breve nuestro equipo de desarrollo se pondrá en contacto contigo. Gracias por confiar en MARKETOS.'
        });
      }
      return response;
    });
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      // silently fail
    }
  };

  const refreshStats = () => loadStats();

  const handleBgColorChange = (color) => {
    setBgColor(color);
    localStorage.setItem('pos_background_color', color);
  };

  // ⭐ Orden del menú (pedido de Angelo, 2026-08-05, corregido el mismo día
  // -- el pedido original decía "que suban", pero era al revés):
  // 1) Conversaciones y Workflows van ABAJO, justo seguido de Configuración.
  // 2) Configuración sigue siendo el ÚLTIMO ítem del menú, sin excepción.
  const tabs = [
    {
      id: 'dashboard-crm',
      label: 'Dashboard CRM',
      icon: <LayoutDashboard className="w-5 h-5" />,
      // ⭐ Entrada del addon CRM (pedido de Angelo, 2026-08-05: "un nuevo
      // módulo... con las pocas gráficas referentes a las funciones
      // actuales que tenemos"). Mismo gate que el resto del grupo CRM.
      // ⭐ Movido al PRIMER lugar del menú (pedido de Angelo, 2026-08-08).
      // Ojo: esto cambia el ORDEN, no la pantalla de arranque -- una cuenta
      // POS sigue abriendo en Caja (ver activeTab arriba), porque un
      // restaurante entra al sistema para cobrar, no para mirar un tablero.
      // Solo las cuentas CRM-only arrancan en este tab.
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'caja',
      label: 'Caja',
      icon: <ShoppingCart className="w-5 h-5" />,
      show: isOwner || permissions.caja
    },
    {
      id: 'pedidos',
      label: 'Pedidos',
      icon: <ClipboardList className="w-5 h-5" />,
      show: isOwner || permissions.pedidos
    },
    {
      id: 'cocina',
      label: 'Cocina',
      icon: <UtensilsCrossed className="w-5 h-5" />,
      minPlan: 'estandar',
      show: (isOwner || permissions.cocina) && planAtLeast('estandar')
    },
    {
      id: 'mesas',
      label: 'Mesas',
      icon: <LayoutGrid className="w-5 h-5" />,
      show: isOwner || permissions.mesas
    },
    {
      id: 'alertas',
      label: 'Alertas',
      icon: <Bell className="w-5 h-5" />,
      show: isOwner || permissions.productos
    },
    {
      id: 'productos',
      label: 'Inventario',
      icon: <Package className="w-5 h-5" />,
      show: isOwner || permissions.productos
    },
    {
      id: 'categorias',
      label: 'Categorías',
      icon: <Tag className="w-5 h-5" />,
      show: isOwner || permissions.categorias
    },
    {
      id: 'reportes',
      label: 'Reportes',
      icon: <BarChart3 className="w-5 h-5" />,
      show: isOwner || permissions.reportes
    },
    {
      id: 'contactos',
      label: 'Contactos',
      icon: <Users className="w-5 h-5" />,
      minPlan: 'estandar',
      // ⭐ Renombrado de "Remarketing" (ver Contactos.js): ya no es comingSoon,
      // así que respeta el plan-gating igual que Cocina/Empleados. Excepción:
      // cuentas CRM-only (isCrmOnly) siempre lo ven, sin importar `plan` --
      // ese campo queda en 'basico' desde el registro standalone y no debe
      // bloquear una sección que sí es parte del CRM que están pagando.
      show: isOwner && (planAtLeast('estandar') || isCrmOnly)
    },
    {
      id: 'empleados',
      label: 'Empleados',
      icon: <Users className="w-5 h-5" />,
      minPlan: 'estandar',
      // ⭐ Cuentas CRM-only NO tienen `clientData.plan` real (queda fijo en
      // 'basico' desde el registro standalone, ver comentario de isCrmOnly
      // más arriba) -- planAtLeast('estandar') siempre daría false y esta
      // rama nunca se habilitaría aunque el id ya esté en
      // CRM_ONLY_TAB_IDS. Se decide por crm_plan_tier en su lugar.
      show: isOwner && (isCrmOnly ? CRM_EMPLOYEES_MIN_TIER.has(clientData?.crmPlanTier) : planAtLeast('estandar'))
    },
    {
      id: 'auditoria',
      label: 'Auditoría',
      icon: <History className="w-5 h-5" />,
      // ⭐ Solo dueño, sin plan-gating: es un registro de seguridad/control
      // interno (quién anuló ventas, cambió precios o ajustó stock), no
      // una función comercial que deba reservarse a planes superiores.
      show: isOwner
    },
    {
      id: 'menu-web',
      label: 'Menu Web Estático',
      icon: <Globe className="w-5 h-5" />,
      minPlan: 'premium',
      // ⭐ Ya no es comingSoon: primera version real (ver MenuWebManager.js
      // + MenuPublico.js). Igual que Contactos, respeta plan-gating.
      // ⭐ 2026-08-08: además del plan, ahora también lo habilita el switch del
      // admin (menu_estatico_enabled). Es un OR, no un reemplazo: quien tiene
      // Premium lo sigue viendo sin que nadie active nada.
      show: isOwner && (planAtLeast('premium') || menuEstaticoAccess)
    },
    {
      id: 'paginas',
      label: 'Páginas Web',
      icon: <Globe className="w-5 h-5" />,
      // ⭐ Constructor de Páginas Web (2026-08-08). ESTE es el tab que reemplaza
      // al viejo "Menu Web Dinámico" que era `comingSoon: true` y no hacía nada
      // -- de ahí que Angelo activara el permiso desde el admin y "no funcionara":
      // el switch estaba bien, lo que no existía era la funcionalidad detrás.
      // Ahora el mismo `menu_dinamico_enabled` enciende el constructor real.
      //
      // Addon puro, sin minPlan a propósito: no se vende por plan ni entra en la
      // prueba gratuita, se activa por cuenta desde el panel de admin. El permiso
      // 'paginas' permite además delegarlo a un empleado (checkbox real en
      // EmpleadosManager.js; el backend lo exige con requirePermission('paginas')).
      show: (isOwner || permissions.paginas) && menuDinamicoAccess
    },
    {
      id: 'suscripcion',
      label: 'Suscripción / Add-ons',
      icon: <CreditCard className="w-5 h-5" />,
      minPlan: 'basico',
      show: isOwner,
      comingSoon: true
    },
    {
      id: 'soporte',
      label: 'Soporte',
      icon: <TicketCheck className="w-5 h-5" />,
      minPlan: 'basico',
      show: isOwner
    },
    {
      id: 'portal-cliente',
      label: isServicesOnly ? 'Mis servicios' : 'Mi proyecto',
      icon: <Sparkles className="w-5 h-5" />,
      show: isOwner
    },
    {
      id: 'facturas',
      label: 'Facturas',
      icon: <FileText className="w-5 h-5" />,
      minPlan: 'basico',
      show: isOwner
    },
    {
      id: 'conversaciones',
      label: 'Conversaciones',
      icon: <MessageCircle className="w-5 h-5" />,
      // ⭐ No usa planAtLeast(): el gate real es hasCrmAccess() en el backend
      // (Premium+/activo O crm_enabled manual para venta standalone), ya
      // resuelto en crmAccess vía GET /api/pos/crm/access.
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'pipeline',
      label: 'Pipeline',
      icon: <Kanban className="w-5 h-5" />,
      // ⭐ Pipeline ahora es su propio tab (pedido de Angelo, 2026-08-05:
      // "que no esté dentro de conversaciones") -- antes era un toggle
      // Chats/Pipeline dentro de Conversaciones.js, ver PipelineManager.js.
      // Mismo gate que Conversaciones/Workflows: sigue siendo parte del
      // mismo addon CRM, no se vende por separado.
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'workflows',
      label: 'Workflows',
      icon: <Workflow className="w-5 h-5" />,
      // ⭐ Mismo gate que Conversaciones -- vive del mismo acceso al CRM
      // (Premium+/activo o crm_enabled manual), no es una sección aparte
      // que se venda por separado.
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'plantillas',
      label: 'Plantillas',
      icon: <FileText className="w-5 h-5" />,
      // ⭐ Sección propia (2026-08-09). Antes las plantillas solo existían como
      // un modal dentro de Workflows, aunque también se usan desde
      // Conversaciones -- esconderlas ahí obligaba a entrar a un workflow para
      // editar un mensaje que no tiene nada que ver con automatizaciones.
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'contextos-ia',
      label: 'Contextos IA',
      icon: <Sparkles className="w-5 h-5" />,
      // ⭐ Sección propia (pedido de Angelo, 2026-08-10). El contexto de la IA
      // vivía dentro del nodo de cada workflow: cambiarlo obligaba a abrir
      // Workflows, encontrar el nodo y guardar. Un cliente real no hace eso, así
      // que ahora es una entidad con nombre propio, igual que las plantillas.
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'campos-personalizados',
      label: 'Campos personalizados',
      icon: <Braces className="w-5 h-5" />,
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'sedes',
      label: 'Sedes',
      icon: <MapPin className="w-5 h-5" />,
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'etiquetas',
      label: 'Etiquetas',
      icon: <Tags className="w-5 h-5" />,
      // ⭐ Sección propia del sidebar (pedido de Angelo, 2026-08-07: "saca
      // las etiquetas de configuración y colócalo en el dashboard pos") --
      // antes vivía como tab dentro de Configuracion.js. Mismo gate que el
      // resto del grupo CRM: sigue siendo parte del addon, no una sección
      // que se venda aparte.
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'calendarios',
      label: 'Calendarios',
      icon: <CalendarIcon className="w-5 h-5" />,
      // ⭐ Módulo de Calendarios (pedido de Angelo, 2026-08-07): mismo gate
      // que el resto del grupo CRM -- es parte del addon, no algo que se
      // venda aparte. Ver CalendariosManager.js.
      show: (isOwner || permissions.crm) && crmAccess
    },
    {
      id: 'configuracion',
      label: 'Configuracion',
      icon: <Settings className="w-5 h-5" />,
      minPlan: 'basico',
      // ⭐ Último ítem del menú a propósito (pedido explícito de Angelo).
      show: isOwner
    },
  ]
    .filter(tab => tab.show)
    // ⭐ Cuentas CRM-only: whitelist final, aplicada DESPUÉS del filtro de
    // `show` normal (pedido explícito de Angelo, 2026-08-06: "que al hacer
    // login les muestre netamente solo lo correspondiente al CRM desarrollado,
    // no coloques nada en próximamente"). Nunca ven Caja/Mesas/Cocina/
    // Inventario/Empleados/Menu Web/etc, ni sus tabs "Próximamente" -- solo
    // las secciones reales del CRM más Configuración y Soporte.
    .filter(tab => !isCrmOnly || CRM_ONLY_TAB_IDS.has(tab.id))
    .filter(tab => !isServicesOnly || SERVICES_ONLY_TAB_IDS.has(tab.id));

  useEffect(() => {
    if (!tabs.find(t => t.id === activeTab) && tabs.length > 0) {
      // ⭐ Mismo criterio que el default de arriba: si el tab activo dejó de
      // ser válido (ej. cambiaron los permisos), una cuenta CRM debe volver
      // a "Dashboard CRM" primero que nada si sigue disponible -- recién si
      // no lo está, cae al primero de la lista como antes.
      const fallback = isServicesOnly
        ? tabs.find(t => t.id === 'portal-cliente') || tabs[0]
        : tabs.find(t => t.id === 'dashboard-crm') || tabs[0];
      setActiveTab(fallback.id);
    }
  }, [activeTab, isServicesOnly, tabs]);

  const COMING_SOON_MESSAGES = {
    empleados: {
      title: 'Gestion de Empleados',
      message: 'Muy pronto podras gestionar tu equipo de trabajo desde aqui!\n\nEsta funcion te permitira crear empleados con acceso al sistema, asignar permisos personalizados (caja, cocina, mesas, reportes, etc.) y controlar quien puede hacer que en tu negocio.\n\nEstamos trabajando para traerte esta herramienta muy pronto!',
      gradient: 'from-green-500 to-teal-500'
    },
    'menu-web-dinamico': {
      title: 'Menu Web Dinámico',
      message: 'Muy pronto tu menu web se podra sincronizar en tiempo real con tu inventario!\n\nCuando un producto se agote o cambie de precio en tu POS, tu menu digital se actualizara automaticamente, sin que tengas que tocar nada manualmente.\n\nEsta funcion llegara muy pronto!',
      gradient: 'from-indigo-500 to-blue-500'
    },
    suscripcion: {
      title: 'Suscripción / Add-ons',
      message: 'Muy pronto podras gestionar tu plan directamente desde aqui!\n\nEsta seccion te permitira ver tu plan actual, cambiar entre planes, comprar mesas/productos/empleados extra sin necesidad de subir de plan, gestionar tu metodo de pago y ver el historial de facturacion.\n\nActualmente estamos integrando los pagos para darte una experiencia segura y automatizada!',
      gradient: 'from-yellow-500 to-amber-500'
    },
    facturas: {
      title: 'Facturacion Electronica',
      message: 'Muy pronto podras generar facturas electronicas directamente desde tu sistema POS!\n\nEsta increible funcion te permitira emitir facturas automaticas, llevar un registro fiscal organizado y cumplir con todas las normativas tributarias.\n\nEstamos trabajando para traerte esta gran herramienta muy pronto!',
      gradient: 'from-blue-500 to-cyan-500'
    }
  };

  const handleTabClick = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab?.comingSoon) {
      setComingSoon(COMING_SOON_MESSAGES[tabId] || null);
      setMobileMenuOpen(false);
      return;
    }
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  // ⭐ Modo kiosko: si un empleado tiene un unico permiso real (ej. Cocinero
  // que solo ve Cocina), no tiene sentido mostrarle el sidebar completo del
  // negocio (nombre, logo, otras secciones que igual no puede tocar). Se
  // bloquea a esa unica pantalla a pantalla completa, mas apropiado para un
  // tablet/estacion fija.
  const isKioskMode = isEmployee && tabs.length === 1;

  // ⭐ Antes el contenido principal se elegia solo por `activeTab`, sin
  // volver a chequear permisos: un empleado sin NINGUN permiso asignado
  // (o cuyo activeTab quedo "atascado" en 'caja' por defecto) igual veia
  // el modulo de Caja renderizado por completo. Ahora el contenido solo
  // se muestra si el tab activo esta realmente en la lista de tabs
  // permitidos para este usuario.
  const activeTabAllowed = tabs.some(t => t.id === activeTab);

  const mainContent = !activeTabAllowed ? (
    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 gap-2 px-4">
      <p className="text-lg font-semibold">No tienes acceso a ninguna sección</p>
      <p className="text-sm">Contacta al dueño del negocio para que te asigne permisos.</p>
    </div>
  ) : (
    <>
      {activeTab === 'caja' && (
        <CajaVentas token={token} onSaleComplete={refreshStats} clientData={clientData} onNavigateTo={setActiveTab} />
      )}
      {activeTab === 'pedidos' && (
        <PedidosPendientes token={token} clientData={clientData} onNavigateTo={setActiveTab} />
      )}
      {activeTab === 'cocina' && (
        <CocinaDisplay token={token} clientData={clientData} />
      )}
      {activeTab === 'mesas' && (
        <MesasManager token={token} clientData={clientData} />
      )}
      {activeTab === 'alertas' && (
        <AlertasManager token={token} />
      )}
      {activeTab === 'dashboard-crm' && (
        <DashboardCRM />
      )}
      {activeTab === 'conversaciones' && (
        <Conversaciones />
      )}
      {activeTab === 'pipeline' && (
        <PipelineManager onOpenConversations={() => setActiveTab('conversaciones')} />
      )}
      {activeTab === 'workflows' && (
        <WorkflowsManager />
      )}
      {activeTab === 'plantillas' && (
        <PlantillasManager />
      )}
      {activeTab === 'contextos-ia' && (
        <ContextosIAManager />
      )}
      {activeTab === 'campos-personalizados' && <CustomFieldsManager />}
      {activeTab === 'sedes' && <LocationsManager />}
      {activeTab === 'etiquetas' && (
        <EtiquetasManager />
      )}
      {activeTab === 'calendarios' && (
        <CalendariosManager />
      )}
      {activeTab === 'contactos' && (
        <Contactos token={token} />
      )}
      {activeTab === 'paginas' && (
        <PaginasManager />
      )}
      {activeTab === 'menu-web' && (
        <MenuWebManager token={token} />
      )}
      {activeTab === 'productos' && (
        <ProductosManager token={token} clientData={clientData} />
      )}
      {activeTab === 'categorias' && (
        <CategoriasManager token={token} clientData={clientData} />
      )}
      {activeTab === 'reportes' && (
        <ReportesUnificado token={token} clientData={clientData} />
      )}
      {activeTab === 'soporte' && (
        <SoporteTickets token={token} clientId={clientData?.id} plan={clientData?.plan} businessName={clientData?.business_name} />
      )}
      {activeTab === 'portal-cliente' && <PortalCliente />}
      {activeTab === 'facturas' && <FacturasManager />}
      {activeTab === 'empleados' && (
        <EmpleadosManager token={token} clientData={clientData} />
      )}
      {activeTab === 'auditoria' && (
        <AuditoriaLog token={token} />
      )}
      {activeTab === 'configuracion' && (
        <Configuracion
          token={token}
          clientData={clientData}
          onProfileUpdate={onProfileUpdate}
          onBgColorChange={handleBgColorChange}
        />
      )}
    </>
  );

  if (isKioskMode) {
    return (
      <div className="h-screen overflow-hidden flex flex-col" style={{ backgroundColor: bgColor }}>
        {managedChangeNotice && (
          <CustomAlert {...managedChangeNotice} zIndex={80} onClose={() => setManagedChangeNotice(null)} />
        )}
        {/* Barra minima: solo rol + cerrar sesion, sin nombre/plan del negocio ni otras secciones */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-900 text-white flex-shrink-0">
          <span className="text-sm font-bold flex items-center gap-2 truncate">
            👤 {clientData?.name}
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-900 border border-emerald-500 text-emerald-300 capitalize">
              {employeeRoleLabel}
            </span>
          </span>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 text-xs font-medium text-red-300 hover:text-red-200 px-3 py-1.5 rounded-lg hover:bg-red-900/40 transition-colors flex-shrink-0"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {mainContent}
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: bgColor }}>
      {managedChangeNotice && (
        <CustomAlert {...managedChangeNotice} zIndex={80} onClose={() => setManagedChangeNotice(null)} />
      )}

      {/* Overlay móvil */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar lateral */}
      <aside
        className={`
          fixed lg:relative z-30 flex flex-col h-full
          bg-gray-900 text-white transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'w-16' : 'w-56'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo / Nombre negocio */}
        <div className="flex items-center justify-between px-3 py-4 border-b border-gray-700 min-h-[64px]">
          {!sidebarCollapsed && (
            <div className="overflow-hidden">
              <p className="text-base font-extrabold text-white truncate leading-tight">
                {clientData?.business_name || clientData?.name || 'POS'}
              </p>
              {isEmployee ? (
                <span className="inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full border capitalize truncate bg-emerald-900 border-emerald-500 text-emerald-300">
                  👤 {employeeRoleLabel}
                </span>
              ) : isCrmOnly ? (
                // ⭐ Fix (pedido de Angelo, 2026-08-08: "aunq actualize un
                // plan de un CRM, sigue apareciendome basico"): antes esta
                // etiqueta siempre leía clientData.plan, que para cuentas
                // CRM-only queda fijo en 'basico' desde el registro (ver
                // comentario más abajo) -- el plan real de estas cuentas es
                // clientData.crmPlanTier (ver CRM_PLAN_TIERS en Dashboard.js
                // para las opciones que el admin puede asignar).
                <span className={`inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full border capitalize truncate
                  ${clientData?.crmPlanTier === 'business' ? 'bg-yellow-900 border-yellow-500 text-yellow-300' :
                    clientData?.crmPlanTier === 'ia_plus' ? 'bg-blue-900 border-blue-500 text-blue-300' :
                    'bg-gray-700 border-gray-500 text-gray-300'}`}>
                  ✦ {CRM_PLAN_TIER_LABELS[clientData?.crmPlanTier] || 'Emprendedor'}
                </span>
              ) : isServicesOnly ? (
                <span className="inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full border truncate bg-sky-900 border-sky-500 text-sky-200">
                  ✦ Servicios MARKETOS
                </span>
              ) : (
                <span className={`inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full border capitalize truncate
                  ${clientData?.plan === 'premium' ? 'bg-purple-900 border-purple-500 text-purple-300' :
                    clientData?.plan === 'estandar' ? 'bg-blue-900 border-blue-500 text-blue-300' :
                    clientData?.plan === 'empresarial' ? 'bg-yellow-900 border-yellow-500 text-yellow-300' :
                    'bg-gray-700 border-gray-500 text-gray-300'}`}>
                  ✦ Plan {clientData?.plan}
                </span>
              )}
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex p-1.5 rounded-lg hover:bg-gray-700 transition-colors ml-auto text-gray-400 hover:text-white"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Ventas del día — solo visible para quien puede ver dinero/reportes.
            ⭐ Oculto en cuentas CRM-only (2026-08-08): ahí no se registra ni una
            venta, así que el contador siempre mostraba $0.00 / 0 transacciones y
            lo único que lograba era hacer dudar al dueño de si el sistema estaba
            fallando. El valor del pipeline vive en el Dashboard CRM, que es su
            lugar correcto, y no se mezcla con dinero realmente cobrado. */}
        {!sidebarCollapsed && !isCrmOnly && !isServicesOnly && (isOwner || permissions.caja || permissions.reportes) && (
          <div className="px-3 py-3 border-b border-gray-700">
            <p className="text-xs text-gray-400">Ventas de hoy</p>
            <p className="text-lg font-bold text-green-400">
              ${(stats.total_revenue || 0).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500">{stats.total_sales} transacciones</p>
          </div>
        )}

        {/* Navegación */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              title={sidebarCollapsed ? tab.label : ''}
              className={`
                w-full flex items-center gap-3 px-2 py-2.5 rounded-lg
                text-sm font-medium transition-all duration-150
                ${activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}
              `}
            >
              <span className="flex-shrink-0">{tab.icon}</span>
              {!sidebarCollapsed && (
                <>
                  <span className="truncate">{tab.label}</span>
                  {tab.comingSoon && (
                    <span className="ml-auto text-[10px] font-bold bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 px-1.5 py-0.5 rounded-full leading-none">
                      Pronto
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </nav>

        {/* Info empleado + logout */}
        <div className="border-t border-gray-700 p-2 space-y-1">
          {!sidebarCollapsed && isEmployee && (
            <p className="text-xs text-blue-400 px-2 py-1 truncate">
              👤 {clientData?.name}
            </p>
          )}
          <button
            onClick={onLogout}
            title={sidebarCollapsed ? 'Cerrar sesión' : ''}
            className={`
              w-full flex items-center gap-3 px-2 py-2.5 rounded-lg
              text-sm font-medium text-red-400 hover:bg-red-900 hover:text-red-200
              transition-colors
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      {/* Contenido principal */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Top bar móvil */}
        <header className="lg:hidden bg-white border-b border-gray-200 flex items-center gap-3 px-4 py-3 flex-shrink-0">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900 truncate">
              {clientData?.business_name || clientData?.name}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Hoy</p>
            <p className="text-sm font-bold text-green-600">
              ${(stats.total_revenue || 0).toFixed(2)}
            </p>
          </div>
        </header>

        {/* ⭐ MODAL: COMING SOON */}
        {comingSoon && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[80] p-4" onClick={() => setComingSoon(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-bounce-once" onClick={(e) => e.stopPropagation()}>
              <div className={`bg-gradient-to-r ${comingSoon.gradient} p-6 text-center`}>
                <Sparkles className="w-16 h-16 text-white mx-auto mb-3 animate-pulse" />
                <h3 className="text-2xl font-black text-white">{comingSoon.title}</h3>
                <div className="mt-2 inline-block bg-white bg-opacity-20 rounded-full px-4 py-1">
                  <span className="text-white font-bold text-sm">✨ Próximamente ✨</span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-700 whitespace-pre-line text-sm leading-relaxed">{comingSoon.message}</p>
                <button
                  onClick={() => setComingSoon(null)}
                  className={`w-full mt-5 py-3 bg-gradient-to-r ${comingSoon.gradient} text-white font-bold rounded-xl hover:opacity-90 transition text-lg`}
                >
                  ¡Entendido, lo espero! 🙌
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Área de contenido con scroll */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {mainContent}
        </main>
      </div>
    </div>
  );
}

export default DashboardPOS;
